import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import communityImage from '../assets/images/community.jpg';

const Community: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });

  const testimonials = [
    {
      id: 1,
      name: "Marie Dupont",
      role: "Maman de 2 enfants",
      quote: "Grâce à cette plateforme, mes enfants ont découvert leur passion pour la musique. Les activités sont variées et de grande qualité !",
      avatar: "https://randomuser.me/api/portraits/women/23.jpg"
    },
    {
      id: 2,
      name: "Thomas Laurent",
      role: "Papa d'un enfant de 6 ans",
      quote: "Un excellent moyen de trouver des activités enrichissantes pour mon fils. La communauté partage des retours d'expérience très utiles.",
      avatar: "https://randomuser.me/api/portraits/men/54.jpg"
    },
    {
      id: 3,
      name: "Sophie Martin",
      role: "Maman de 3 enfants",
      quote: "Je recommande vivement ! Les ateliers créatifs ont permis à mes enfants de développer leur imagination tout en s'amusant.",
      avatar: "https://randomuser.me/api/portraits/women/45.jpg"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="community" className="py-20 relative" ref={ref}>
      {/* Background avec effet parallaxe */}
      <div className="absolute inset-0 z-0">
        <div className="h-full w-full bg-cover bg-fixed bg-center opacity-20" style={{ 
          backgroundImage: `url(${communityImage})` 
        }} />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-[#F5F5F5] to-white" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#1C1C1C] mb-4">
            Notre <span className="text-[#87CEFA]">communauté</span> de familles
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Rejoignez des parents partageant les mêmes valeurs et échangez des expériences enrichissantes pour vos enfants.
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {testimonials.map((testimonial) => (
            <motion.div 
              key={testimonial.id}
              className="bg-white rounded-xl shadow-lg p-6 flex flex-col"
              variants={itemVariants}
              whileHover={{ y: -10, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
            >
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full overflow-hidden mr-4">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name} 
                    className="h-full w-full object-cover"
                  />
                </div>
                <div>
                  <h4 className="font-bold text-[#1C1C1C]">{testimonial.name}</h4>
                  <p className="text-sm text-gray-500">{testimonial.role}</p>
                </div>
              </div>
              <p className="text-gray-600 flex-grow">"{testimonial.quote}"</p>
              <div className="flex text-[#FFD700] mt-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          className="bg-[#87CEFA] bg-opacity-20 rounded-xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="mb-6 md:mb-0 md:mr-8">
            <h3 className="text-2xl font-bold text-[#1C1C1C] mb-2">Rejoignez notre forum</h3>
            <p className="text-lg text-gray-600">
              Échangez avec d'autres parents, partagez vos expériences et découvrez de nouvelles activités recommandées par la communauté.
            </p>
          </div>
          <motion.button 
            className="bg-[#1C1C1C] hover:bg-[#333333] text-white font-bold py-3 px-8 rounded-lg shadow-md transition-colors whitespace-nowrap"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Accéder au forum
          </motion.button>
        </motion.div>

        <motion.div 
          className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          {['Discussions récentes', 'Recommandations', 'Événements communautaires', 'Groupes thématiques'].map((item, index) => (
            <motion.div 
              key={index}
              className="bg-white p-4 rounded-lg shadow-sm"
              whileHover={{ 
                backgroundColor: "#F0F8FF",
                transition: { duration: 0.2 } 
              }}
            >
              <h4 className="font-semibold text-[#1C1C1C] mb-2">{item}</h4>
              <div className="bg-gray-200 h-2 w-full rounded-full">
                <motion.div 
                  className="bg-[#87CEFA] h-2 rounded-full" 
                  initial={{ width: 0 }}
                  animate={inView ? { width: `${65 + index * 10}%` } : { width: 0 }}
                  transition={{ duration: 1, delay: 0.8 + index * 0.1 }}
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">{12 + index * 8} contributions cette semaine</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Community;
